import { DOCUMENT } from '@angular/common';
import { ChangeDetectionStrategy, Component, OnInit, inject, input, output, signal } from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInput } from '@angular/material/input';
import { EmailValidators } from '@iot-platform/iot-platform-utils';
import { TranslatePipe } from '@ngx-translate/core';
// import { ProgressBarButtonModule } from '@iot-platform/iot-platform-ui';
// FIXME this dependency should be reviewed it causes an app crash
import { ProgressBarButtonModule } from '../../../../../iot-platform/iot-platform-ui/src/lib/components/progress-bar-button/progress-bar-button.module';

const REMEMBER_ME_KEY = 'rememberMe';
const REMEMBERED_EMAIL_COOKIE = 'rememberedEmail';
const COOKIE_MAX_AGE = 365 * 24 * 60 * 60;

@Component({
  imports: [TranslatePipe, ReactiveFormsModule, MatFormFieldModule, MatIconModule, MatInput, MatButtonModule, MatCheckboxModule, ProgressBarButtonModule],
  selector: 'iot-platform-login-form',
  templateUrl: './login-form.component.html',
  styleUrls: ['./login-form.component.scss', '../../scss/style.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class LoginFormComponent implements OnInit {
  private readonly document = inject(DOCUMENT);

  hasError = input<boolean>(false);
  isI4BLogin = input<boolean>(false);
  ssoDisabled = input<boolean>(false);
  rememberMe = input<boolean>(false);

  enableGauge = signal(false);
  hide = signal(true);

  resetPassword = output<void>();
  signIn = output<{ username: string; password: string }>();

  // eslint-disable-next-line @angular-eslint/no-output-native
  cancel = output<void>();
  rememberMeChange = output<boolean>();

  form = new FormGroup({
    email: new FormControl<string | null>(null, [Validators.required, EmailValidators.isValid()]),
    password: new FormControl<string | null>(null, [Validators.required]),
    rememberMe: new FormControl(false, { nonNullable: true })
  });

  ngOnInit(): void {
    const saved = this.document.defaultView?.localStorage.getItem(REMEMBER_ME_KEY);
    if (saved !== null && saved !== undefined) {
      this.form.get('rememberMe')?.setValue(saved === 'true');
    }
    const savedEmail = this.getEmailFromCookie();
    if (savedEmail) {
      this.form.get('email')?.setValue(savedEmail);
      this.form.get('rememberMe')?.setValue(true);
    }
  }

  get email(): FormControl<string | null> {
    return this.form.controls.email;
  }

  get password(): FormControl<string | null> {
    return this.form.controls.password;
  }

  onHide() {
    this.hide.update((h) => !h);
  }

  onChangeEmail(value: string): void {
    const lowered = value.toLowerCase();
    this.email.setValue(lowered);
    if (this.form.get('rememberMe')?.value) {
      this.saveEmailToCookie(lowered);
    }
  }

  onResetPassword() {
    this.resetPassword.emit();
  }

  onRememberMeChange(checked: boolean): void {
    this.document.defaultView?.localStorage.setItem(REMEMBER_ME_KEY, String(checked));
    if (checked) {
      this.saveEmailToCookie(this.email.value ?? '');
    } else {
      this.clearEmailCookie();
    }
    this.rememberMeChange.emit(checked);
  }

  onSignIn() {
    if (this.form.valid) {
      this.signIn.emit({ username: this.email.value ?? '', password: this.password.value ?? '' });
    }
  }

  private getEmailFromCookie(): string | null {
    const match = this.document.cookie.split(';').find((c) => c.trim().startsWith(`${REMEMBERED_EMAIL_COOKIE}=`));
    return match ? decodeURIComponent(match.split('=')[1].trim()) : null;
  }

  private saveEmailToCookie(email: string): void {
    this.document.cookie = `${REMEMBERED_EMAIL_COOKIE}=${encodeURIComponent(email)}; path=/; max-age=${COOKIE_MAX_AGE}`;
  }

  private clearEmailCookie(): void {
    this.document.cookie = `${REMEMBERED_EMAIL_COOKIE}=; path=/; max-age=0`;
  }
}
